<script setup lang="ts">
  import { computed, watch } from "vue";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { Button } from "@/components/ui/button";
  import type { JcdBenchmark, JcdBenchmarkPayload } from "@/types";

  const props = defineProps<{
    open: boolean;
    benchmark: JcdBenchmark | null;
  }>();

  const emit = defineEmits<{
    "update:open": [value: boolean];
    submit: [payload: JcdBenchmarkPayload];
  }>();

  const validationSchema = toTypedSchema(
    z.object({
      name: z.string({ required_error: "Le nom est obligatoire" }).min(1, "Le nom est obligatoire"),
      reco: z.number().min(0, "0 à 100").max(100, "0 à 100").nullish(),
      attribution: z.number().min(0, "0 à 100").max(100, "0 à 100").nullish(),
      intention: z.number().min(0, "0 à 100").max(100, "0 à 100").nullish(),
    }),
  );

  const { handleSubmit, meta, resetForm } = useForm({ validationSchema });

  const asPercent = (value: number | null) => (value == null ? null : Math.round(value * 100));
  const asFraction = (value: number | null | undefined) => (value == null ? null : value / 100);

  watch(
    () => props.open,
    (open) => {
      if (!open) return;
      resetForm({
        values: props.benchmark
          ? {
              name: props.benchmark.name,
              reco: asPercent(props.benchmark.reco),
              attribution: asPercent(props.benchmark.attribution),
              intention: asPercent(props.benchmark.intention),
            }
          : { name: "", reco: null, attribution: null, intention: null },
      });
    },
  );

  const hasErrors = computed(() => meta.value.dirty && !meta.value.valid);

  const kpiFields = [
    { name: "reco", label: "Reco (%)" },
    { name: "attribution", label: "Attribution (%)" },
    { name: "intention", label: "Intention (%)" },
  ] as const;

  const onSubmit = handleSubmit((values) => {
    emit("submit", {
      name: values.name,
      reco: asFraction(values.reco),
      attribution: asFraction(values.attribution),
      intention: asFraction(values.intention),
    });
  });
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ benchmark ? "Modifier le benchmark" : "Nouveau benchmark" }}</DialogTitle>
      </DialogHeader>
      <form
        class="space-y-4"
        @submit.prevent="onSubmit">
        <FormField
          v-slot="{ componentField }"
          name="name">
          <FormItem>
            <FormLabel>Nom <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                placeholder="Ex : Boissons 2026" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <div class="grid grid-cols-3 gap-4">
          <FormField
            v-for="kpi in kpiFields"
            :key="kpi.name"
            v-slot="{ componentField }"
            :name="kpi.name">
            <FormItem>
              <FormLabel>{{ kpi.label }}</FormLabel>
              <FormControl>
                <InputNumber
                  v-bind="componentField"
                  :min="0"
                  :max="100"
                  placeholder="—" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <DialogFooter>
          <Button
            type="submit"
            :variant="hasErrors ? 'destructive' : 'default'"
            :disabled="hasErrors">
            Enregistrer
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
